import { useEffect, useMemo, useRef, useState } from "react";
import api, { API } from "@/lib/api";
import { toast } from "sonner";
import ReactMarkdown from "react-markdown";
import remarkGfm from "remark-gfm";
import { Send, Plus, Trash2, PanelRightClose, PanelRightOpen, Copy, Check, Palette, PenLine, Image as ImageIcon, Sparkles } from "lucide-react";
import { useAuth } from "@/contexts/AuthContext";
import { useConfirm } from "@/contexts/ConfirmContext";

const HEX_RE = /#(?:[0-9a-fA-F]{3}){1,2}\b/g;

const QUICK_ACTIONS = [
  { title: "Check Tone of Voice", prompt: "Beschrijf de tone of voice van dit merk met 3 kernwoorden en 2 voorbeeldzinnen.", Icon: PenLine },
  { title: "Find Logo rules", prompt: "Welke regels gelden voor het gebruik van het logo? Noem minimale afmetingen, veilige zones en wat niet mag.", Icon: ImageIcon },
  { title: "Suggest Colors", prompt: "Welke secundaire kleuren passen bij de huidige kleurenpalet van dit merk? Geef de HEX-codes en wanneer je ze gebruikt.", Icon: Palette },
];

export default function BrandAgent({ brand, canUse }) {
  const { user } = useAuth();
  const confirm = useConfirm();
  const [sessions, setSessions] = useState([]);
  const [sessionId, setSessionId] = useState(null);
  const [messages, setMessages] = useState([]);
  const [input, setInput] = useState("");
  const [busy, setBusy] = useState(false);
  const [showRight, setShowRight] = useState(true);
  const scrollRef = useRef(null);
  const inputRef = useRef(null);

  const refreshSessions = async () => {
    try {
      const { data } = await api.get(`/chat/sessions`, { params: { brand_id: brand.id, tool: "agent" } });
      setSessions(data);
    } catch { /* ignore */ }
  };

  useEffect(() => {
    refreshSessions();
    setMessages([]);
    setSessionId(null);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [brand.id]);

  useEffect(() => {
    scrollRef.current?.scrollTo({ top: scrollRef.current.scrollHeight, behavior: "smooth" });
  }, [messages.length]);

  if (!canUse) {
    return (
      <div className="h-screen flex items-center justify-center p-10 bg-white">
        <div className="max-w-md text-center">
          <h1 className="font-display text-3xl mb-3 text-slate-900">The Brand Assistant</h1>
          <p className="text-slate-500">Je rol heeft nog geen toegang. Vraag een manager of owner om een upgrade.</p>
        </div>
      </div>
    );
  }

  const loadSession = async (sid) => {
    setSessionId(sid);
    setMessages([]);
    try {
      const { data } = await api.get(`/chat/history`, { params: { brand_id: brand.id, tool: "agent", session_id: sid } });
      setMessages(data.map((m, i) => ({ ...m, id: `${sid}_${i}` })));
    } catch { toast.error("Chat kon niet geladen worden"); }
  };

  const newChat = () => {
    setSessionId(null);
    setMessages([]);
    setInput("");
    inputRef.current?.focus();
  };

  const deleteSession = async (sid, e) => {
    e.stopPropagation();
    const ok = await confirm({
      title: "Deze chat verwijderen?",
      body: "De berichten worden definitief verwijderd. Dit kan niet ongedaan worden gemaakt.",
      confirmLabel: "Verwijderen",
      tone: "danger",
    });
    if (!ok) return;
    try {
      await api.delete(`/chat/sessions/${sid}`, { params: { brand_id: brand.id, tool: "agent" } });
      if (sid === sessionId) newChat();
      await refreshSessions();
    } catch { toast.error("Verwijderen mislukt"); }
  };

  const send = async () => {
    const text = input.trim();
    if (!text || busy) return;
    const activeSid = sessionId || `agent_${brand.id}_${user?.user_id}_${Date.now().toString(36)}`;
    if (!sessionId) setSessionId(activeSid);
    setInput("");
    setMessages((m) => [
      ...m,
      { role: "user", content: text, id: Date.now() + "u" },
      { role: "assistant", content: "", id: Date.now() + "a", streaming: true },
    ]);
    setBusy(true);
    try {
      const token = localStorage.getItem("bh_token");
      const res = await fetch(`${API}/chat/agent`, {
        method: "POST",
        credentials: "include",
        headers: { "Content-Type": "application/json", ...(token ? { Authorization: `Bearer ${token}` } : {}) },
        body: JSON.stringify({ brand_id: brand.id, message: text, session_id: activeSid }),
      });
      if (!res.ok) throw new Error("stream failed");
      const reader = res.body.getReader();
      const decoder = new TextDecoder();
      let buffer = "";
      let acc = "";
      while (true) {
        const { done, value } = await reader.read();
        if (done) break;
        buffer += decoder.decode(value, { stream: true });
        const parts = buffer.split("\n\n");
        buffer = parts.pop();
        for (const part of parts) {
          const line = part.trim();
          if (!line.startsWith("data:")) continue;
          const payload = line.slice(5).trim();
          if (payload === "[DONE]") continue;
          try {
            const obj = JSON.parse(payload);
            if (obj.delta) {
              acc += obj.delta;
              setMessages((m) => {
                const next = [...m];
                next[next.length - 1] = { ...next[next.length - 1], content: acc };
                return next;
              });
            }
          } catch { /* ignore */ }
        }
      }
      setMessages((m) => {
        const next = [...m];
        next[next.length - 1] = { ...next[next.length - 1], streaming: false };
        return next;
      });
      refreshSessions();
    } catch {
      toast.error("Kon niet reageren");
    } finally {
      setBusy(false);
    }
  };

  const firstName = (user?.name || user?.email || "").split(" ")[0] || "there";
  const isEmpty = messages.length === 0;

  return (
    <div className="h-screen w-full flex bg-white text-slate-900 overflow-hidden" style={{ fontFamily: 'Inter, ui-sans-serif, system-ui, sans-serif' }}>
      {/* Center */}
      <div className="flex-1 flex flex-col min-w-0 relative">
        <header className="h-14 shrink-0 border-b border-slate-100 flex items-center justify-between px-4 lg:px-6">
          <div className="flex items-center gap-2.5 min-w-0">
            <div className="w-7 h-7 rounded-md bg-slate-900 text-white flex items-center justify-center">
              <Sparkles className="w-3.5 h-3.5" />
            </div>
            <div className="truncate">
              <div className="text-sm font-medium text-slate-900 truncate">The Brand Assistant</div>
              <div className="text-xs text-slate-500 truncate">{brand.name}</div>
            </div>
          </div>
          <button
            onClick={() => setShowRight((s) => !s)}
            data-testid="toggle-history"
            className="hidden md:inline-flex items-center gap-2 h-8 px-3 rounded-md text-xs font-medium text-slate-600 hover:bg-slate-100"
          >
            {showRight ? <PanelRightClose className="w-4 h-4" /> : <PanelRightOpen className="w-4 h-4" />}
            History
          </button>
        </header>

        <div ref={scrollRef} className="flex-1 overflow-y-auto bh-scroll">
          {isEmpty ? (
            <div className="h-full flex items-center justify-center px-6">
              <div className="max-w-2xl w-full">
                <h1 className="text-4xl md:text-5xl font-medium tracking-tight text-slate-900 text-center">
                  Hi {firstName},
                </h1>
                <p className="mt-3 text-lg text-slate-500 text-center max-w-xl mx-auto">
                  I answer all the questions you have about the brand or The Brand Hub.
                </p>
                <div className="mt-10 grid grid-cols-1 sm:grid-cols-3 gap-3">
                  {QUICK_ACTIONS.map(({ title, prompt, Icon }) => (
                    <button
                      key={title}
                      onClick={() => { setInput(prompt); inputRef.current?.focus(); }}
                      className="group aspect-square rounded-xl border border-slate-200 bg-white hover:shadow-sm hover:border-slate-300 transition p-5 flex flex-col items-start justify-between text-left"
                      data-testid={`quick-${title.toLowerCase().replace(/\s+/g, "-")}`}
                    >
                      <div className="w-9 h-9 rounded-md bg-slate-100 flex items-center justify-center">
                        <Icon className="w-4 h-4 text-slate-700" />
                      </div>
                      <div>
                        <div className="text-sm font-medium text-slate-900">{title}</div>
                        <div className="text-xs text-slate-500 mt-1 line-clamp-2">{prompt}</div>
                      </div>
                    </button>
                  ))}
                </div>
              </div>
            </div>
          ) : (
            <div className="max-w-3xl mx-auto px-4 lg:px-6 py-6 pb-40 space-y-5">
              {messages.map((m) => (
                <ChatBubble key={m.id || m.created_at} msg={m} />
              ))}
            </div>
          )}
        </div>

        {/* Floating composer */}
        <div className="absolute left-0 right-0 bottom-4 px-4 lg:px-6 pointer-events-none">
          <div className="max-w-3xl mx-auto pointer-events-auto">
            <div className="flex items-end gap-2 rounded-2xl shadow-lg border border-slate-200 bg-white p-2">
              <textarea
                ref={inputRef}
                data-testid="agent-input"
                value={input}
                onChange={(e) => setInput(e.target.value)}
                onKeyDown={(e) => { if (e.key === "Enter" && !e.shiftKey) { e.preventDefault(); send(); } }}
                placeholder="Message The Brand Assistant…"
                rows={1}
                className="flex-1 resize-none bg-transparent border-0 outline-none px-3 py-2.5 text-sm text-slate-900 placeholder:text-slate-400 max-h-40"
              />
              <button
                onClick={send}
                disabled={busy || !input.trim()}
                data-testid="agent-send-btn"
                className="h-9 w-9 rounded-xl bg-slate-900 text-white flex items-center justify-center hover:bg-slate-800 disabled:opacity-40 disabled:cursor-not-allowed shrink-0"
              >
                <Send className="w-4 h-4" />
              </button>
            </div>
            <div className="text-[11px] text-slate-400 text-center mt-2">
              Answers can be imperfect — verify important details with your brand book.
            </div>
          </div>
        </div>
      </div>

      {/* Right history */}
      {showRight && (
        <aside className="hidden md:flex w-72 shrink-0 border-l border-slate-100 flex-col bg-slate-50/50">
          <div className="p-3">
            <button
              onClick={newChat}
              data-testid="new-chat-btn"
              className="w-full h-9 inline-flex items-center gap-2 justify-center rounded-md bg-slate-900 text-white hover:bg-slate-800 text-sm font-medium"
            >
              <Plus className="w-4 h-4" /> New chat
            </button>
          </div>
          <div className="flex-1 overflow-y-auto bh-scroll px-2 pb-4">
            {sessions.length === 0 ? (
              <div className="text-xs text-slate-400 px-3 py-6 text-center">No chats yet</div>
            ) : (
              sessions.map((s) => (
                <div
                  key={s.session_id}
                  onClick={() => loadSession(s.session_id)}
                  data-testid={`session-${s.session_id}`}
                  className={`group flex items-center gap-2 px-3 py-2 rounded-md cursor-pointer text-sm ${
                    s.session_id === sessionId
                      ? "bg-slate-200/70 text-slate-900"
                      : "text-slate-700 hover:bg-slate-100"
                  }`}
                >
                  <span className="flex-1 truncate">{s.title}</span>
                  <button
                    onClick={(e) => deleteSession(s.session_id, e)}
                    className="opacity-0 group-hover:opacity-100 p-1 rounded hover:bg-slate-200 text-slate-500 hover:text-red-600 transition"
                    aria-label="Delete"
                  >
                    <Trash2 className="w-3.5 h-3.5" />
                  </button>
                </div>
              ))
            )}
          </div>
        </aside>
      )}
    </div>
  );
}

function ChatBubble({ msg }) {
  const isUser = msg.role === "user";
  return (
    <div className={`flex ${isUser ? "justify-end" : "justify-start"}`}>
      <div
        className={`max-w-[85%] rounded-2xl px-4 py-3 text-[15px] leading-relaxed ${
          isUser ? "bg-slate-900 text-white rounded-br-md" : "bg-gray-50 text-slate-900 rounded-bl-md border border-slate-100"
        }`}
      >
        {isUser ? (
          <div className="whitespace-pre-wrap">{msg.content}</div>
        ) : (
          <>
            <AssistantMarkdown content={msg.content} />
            {msg.streaming && <span className="bh-typing" />}
          </>
        )}
      </div>
    </div>
  );
}

function AssistantMarkdown({ content }) {
  const transformed = useMemo(() => wrapHexCodes(content || ""), [content]);
  return (
    <div className="prose-chat">
      <ReactMarkdown
        remarkPlugins={[remarkGfm]}
        components={{
          h1: ({ node, ...p }) => <h1 className="font-medium text-xl mt-4 mb-2 tracking-tight" {...p} />,
          h2: ({ node, ...p }) => <h2 className="font-medium text-lg mt-4 mb-2 tracking-tight" {...p} />,
          h3: ({ node, ...p }) => <h3 className="font-medium text-base mt-3 mb-1.5 tracking-tight" {...p} />,
          p: ({ node, ...p }) => <p className="my-2" {...p} />,
          strong: ({ node, ...p }) => <strong className="font-semibold" {...p} />,
          em: ({ node, ...p }) => <em className="italic" {...p} />,
          ul: ({ node, ...p }) => <ul className="list-disc ml-5 my-2 space-y-1" {...p} />,
          ol: ({ node, ...p }) => <ol className="list-decimal ml-5 my-2 space-y-1" {...p} />,
          li: ({ node, ...p }) => <li className="leading-relaxed" {...p} />,
          a: ({ node, ...p }) => <a className="underline underline-offset-2 text-slate-900" target="_blank" rel="noreferrer" {...p} />,
          code: InlineOrBlockCode,
          pre: ({ node, children, ...p }) => <pre className="bg-gray-100 border border-gray-200 rounded-lg p-3 my-3 overflow-x-auto text-sm font-mono" {...p}>{children}</pre>,
          blockquote: ({ node, ...p }) => <blockquote className="border-l-2 border-slate-300 pl-3 my-3 text-slate-600" {...p} />,
        }}
      >
        {transformed}
      </ReactMarkdown>
    </div>
  );
}

function wrapHexCodes(md) {
  // Only wrap hex codes that aren't already inside markdown code spans.
  const parts = md.split(/(`[^`]+`|```[\s\S]+?```)/g);
  return parts
    .map((p, i) => (i % 2 === 1 ? p : p.replace(HEX_RE, (match) => `\`${match}\``)))
    .join("");
}

function InlineOrBlockCode({ inline, className, children, ...p }) {
  const value = String(children ?? "").replace(/\n$/, "");
  if (!inline) {
    return <code className={className} {...p}>{children}</code>;
  }
  const isHex = /^#(?:[0-9a-fA-F]{3}){1,2}$/.test(value);
  if (isHex) return <HexBadge value={value} />;
  return (
    <code className="bg-gray-100 border border-gray-200 rounded px-1.5 py-0.5 font-mono text-[0.85em]">
      {children}
    </code>
  );
}

function HexBadge({ value }) {
  const [copied, setCopied] = useState(false);
  const copy = async (e) => {
    e.preventDefault();
    e.stopPropagation();
    try {
      await navigator.clipboard.writeText(value);
      setCopied(true);
      setTimeout(() => setCopied(false), 1200);
    } catch { /* ignore */ }
  };
  return (
    <span className="inline-flex items-center gap-1 align-baseline bg-gray-100 border border-gray-200 rounded px-1.5 py-0.5 font-mono text-[0.85em]">
      <span className="inline-block w-3 h-3 rounded-sm border border-black/10" style={{ backgroundColor: value }} />
      <span>{value}</span>
      <button onClick={copy} className="ml-0.5 text-slate-500 hover:text-slate-900" title={copied ? "Copied" : "Copy"}>
        {copied ? <Check className="w-3 h-3" /> : <Copy className="w-3 h-3" />}
      </button>
    </span>
  );
}

export function PaywallLike({ title, desc }) {
  return (
    <div className="max-w-2xl mx-auto p-12">
      <div className="p-8 rounded-2xl bg-white border border-slate-200">
        <div className="inline-flex items-center gap-2 text-[10px] uppercase tracking-widest px-2 py-1 rounded bg-slate-900 text-white mb-4">Pro</div>
        <h1 className="font-display text-4xl tracking-tight mb-3">{title}</h1>
        <p className="text-slate-500 text-lg mb-6">{desc}</p>
        <div className="text-sm text-slate-500">Je huidige rol heeft geen toegang tot deze functie. Vraag een Manager of Owner om een upgrade.</div>
      </div>
    </div>
  );
}
